import { useEffect, useState } from "react";
import { Pencil, Plus, Repeat } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, euro, dataIt, formatError } from "@/lib/api";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { EntityFormDialog } from "@/components/EntityFormDialog";

const LAVORO_FIELDS = [
  { name: "nome", label: "Nome del lavoro", required: true, full: true },
  { name: "prezzo", label: "Prezzo fisso (€)", type: "number", step: "0.01" },
  { name: "durata_ore", label: "Tempo di lavoro (ore)", type: "number", step: "0.25" },
  { name: "note", label: "Note", type: "textarea" },
];

const Lista = ({ title, sub, testId, onNew, newLabel, children }) => (
  <section className="rounded-lg border border-slate-200 bg-white shadow-sm" data-testid={testId}>
    <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3">
      <div><h2 className="font-heading text-base font-bold">{title}</h2><p className="text-xs text-slate-500">{sub}</p></div>
      <Button size="sm" onClick={onNew} className="shrink-0 bg-orange-600 hover:bg-orange-700" data-testid={`${testId}-new`}><Plus className="mr-1 h-3.5 w-3.5" /> {newLabel}</Button>
    </div>
    <ul className="max-h-96 divide-y divide-slate-100 overflow-y-auto">{children}</ul>
  </section>
);

const useElenco = (endpoint) => {
  const [items, setItems] = useState([]);
  const load = () => api.get(`/${endpoint}`).then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const remove = (id) => api.delete(`/${endpoint}/${id}`).then(load).catch((e) => toast.error(formatError(e)));
  return { items, load, remove };
};

const Riga = ({ children, onEdit, onRemove, testId }) => (
  <li className="flex items-center gap-2 px-5 py-3 text-sm" data-testid={testId}>
    <div className="min-w-0 flex-1">{children}</div>
    <Button variant="ghost" size="icon" onClick={onEdit} data-testid={`${testId}-edit`}><Pencil className="h-4 w-4" /></Button>
    <ConfirmDelete testId={`${testId}-delete`} onConfirm={onRemove} />
  </li>
);

export const LavoriFissiSettings = () => {
  const { items, load, remove } = useElenco("lavori_fissi");
  const [dlg, setDlg] = useState(null);
  return (
    <Lista title="Lavori a prezzo fisso" sub="Si inseriscono con un tocco nelle schede di lavoro e nei preventivi." testId="lavori-fissi-settings" onNew={() => setDlg({})} newLabel="Nuovo lavoro">
      {items.map((l) => (
        <Riga key={l.id} testId={`lavoro-fisso-${l.id}`} onEdit={() => setDlg(l)} onRemove={() => remove(l.id)}>
          <p className="font-semibold">{l.nome}</p><p className="text-xs text-slate-500"><span className="font-mono font-bold text-slate-900">{euro(l.prezzo)}</span>{l.durata_ore ? ` · ${l.durata_ore} ore di lavoro` : ""}</p>
        </Riga>
      ))}
      {items.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-500">Nessun lavoro. Esempio: "Tagliando completo" a 45 €.</li>}
      <EntityFormDialog open={Boolean(dlg)} onOpenChange={(o) => !o && setDlg(null)} endpoint="lavori_fissi" fields={LAVORO_FIELDS} item={dlg?.id ? dlg : null}
        title={dlg?.id ? "Modifica lavoro" : "Nuovo lavoro a prezzo fisso"} prefix="lavoro-fisso-form" onSaved={load} />
    </Lista>
  );
};

const today = () => new Date().toISOString().slice(0, 10);
const VUOTA = () => ({ data: today(), descrizione: "", categoria: "", importo: "", ricorrente: false, fine: "" });
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

const SpesaDialog = ({ item, onClose, onSaved }) => {
  const [f, setF] = useState(VUOTA());
  useEffect(() => { if (item) { setF({ ...VUOTA(), ...item }); } }, [item]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    if (!f.descrizione.trim() || !(Number(f.importo) > 0)) return toast.error("Scrivi descrizione e importo");
    if (f.ricorrente && f.fine && f.fine < f.data) return toast.error("La data di fine deve essere dopo l'inizio");
    const payload = { data: f.data, descrizione: f.descrizione, categoria: f.categoria, importo: Number(f.importo), ricorrente: f.ricorrente, fine: f.ricorrente ? f.fine : "" };
    try {
      await (item.id ? api.put(`/spese/${item.id}`, payload) : api.post("/spese", payload));
      toast.success("Spesa salvata"); onSaved(); onClose();
    } catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Dialog open={Boolean(item)} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="spesa-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{item?.id ? "Modifica spesa" : "Nuova spesa"}</DialogTitle><DialogDescription>Viene tolta dagli incassi per calcolare il guadagno effettivo.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><L>Descrizione *</L><Input value={f.descrizione} onChange={(e) => set("descrizione", e.target.value)} placeholder="Es. Affitto negozio" data-testid="spesa-descrizione-input" /></div>
          <div><L>Categoria</L><Input value={f.categoria} onChange={(e) => set("categoria", e.target.value)} placeholder="Es. Affitto, Bollette" data-testid="spesa-categoria-input" /></div>
          <div><L>Importo (€) *</L><Input type="number" step="0.01" value={f.importo} onChange={(e) => set("importo", e.target.value)} data-testid="spesa-importo-input" /></div>
          <div><L>{f.ricorrente ? "Dal" : "Data"}</L><Input type="date" value={f.data} onChange={(e) => set("data", e.target.value)} data-testid="spesa-data-input" /></div>
          {f.ricorrente && <div><L>Fino al (facoltativo)</L><Input type="date" value={f.fine} onChange={(e) => set("fine", e.target.value)} data-testid="spesa-fine-input" /></div>}
          <label className="flex items-center gap-2.5 text-sm sm:col-span-2"><Checkbox checked={f.ricorrente} onCheckedChange={(v) => set("ricorrente", Boolean(v))} data-testid="spesa-ricorrente-check" />Si ripete ogni mese</label>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-600 hover:bg-orange-700" data-testid="spesa-save-button">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const SpeseSettings = () => {
  const { items, load, remove } = useElenco("spese");
  const [dlg, setDlg] = useState(null);
  return (
    <Lista title="Spese extra" sub="Affitto, bollette, stipendi... servono a calcolare il guadagno effettivo." testId="spese-settings" onNew={() => setDlg({})} newLabel="Nuova spesa">
      {items.map((s) => (
        <Riga key={s.id} testId={`spesa-${s.id}`} onEdit={() => setDlg(s)} onRemove={() => remove(s.id)}>
          <p className="flex items-center gap-1.5 font-semibold">{s.descrizione}{s.ricorrente && <Repeat className="h-3.5 w-3.5 text-orange-600" />}</p>
          <p className="text-xs text-slate-500"><span className="font-mono font-bold text-slate-900">{euro(s.importo)}</span>{s.ricorrente ? ` al mese dal ${dataIt(s.data)}${s.fine ? ` al ${dataIt(s.fine)}` : ""}` : ` · ${dataIt(s.data)}`}{s.categoria ? ` · ${s.categoria}` : ""}</p>
        </Riga>
      ))}
      {items.length === 0 && <li className="px-5 py-8 text-center text-sm text-slate-500">Nessuna spesa inserita.</li>}
      <SpesaDialog item={dlg} onClose={() => setDlg(null)} onSaved={load} />
    </Lista>
  );
};
